import React, { useState, useEffect, useRef } from "react";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { api } from "@/lib/api";
import { runJob } from "@/lib/jobs";
import { Link } from "react-router-dom";
import { Sparkles, Mic, MicOff, Send, Loader2, ArrowRight, ExternalLink, Heart, Wand2 } from "lucide-react";
import { ToolLogo } from "@/components/ToolLogo";
import WikifiMascot from "@/components/WikifiMascot";

/**
 * Wikifi Genius — the empathetic natural-language advisor.
 * User describes any life situation → gets 3 warmly-explained AI tool picks
 * hand-selected by GPT-5.4 from the Wikifi catalog.
 */

const PRESET_SITUATIONS = [
  { emoji: "💸", text: "I want to save money" },
  { emoji: "🧠", text: "I am struggling with my mental health" },
  { emoji: "🎬", text: "Help me build my perfect daily AI stack" },
  { emoji: "📚", text: "I want to learn faster" },
  { emoji: "📈", text: "I need to grow my audience" },
  { emoji: "⚡", text: "I need an AI that does the boring work for me" },
];

export default function AskGenius() {
  const [situation, setSituation] = useState("");
  const [listening, setListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);

  // Feature-detect Web Speech API
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    setVoiceSupported(true);
    const r = new SR();
    r.continuous = false;
    r.interimResults = true;
    r.lang = "en-US";
    r.onresult = (e) => {
      const transcript = Array.from(e.results).map(res => res[0].transcript).join(" ");
      setSituation(transcript);
    };
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    recognitionRef.current = r;
    return () => {
      try { r.stop(); } catch { /* ignore */ }
    };
  }, []);

  const toggleVoice = () => {
    const r = recognitionRef.current;
    if (!r) return;
    if (listening) {
      try { r.stop(); } catch { /* ignore */ }
      setListening(false);
    } else {
      setSituation("");
      try { r.start(); setListening(true); } catch { /* already running */ }
    }
  };

  const ask = async (text) => {
    const q = (text ?? situation).trim();
    if (!q) return;
    setLoading(true); setError(null); setResult(null);
    if (text != null) setSituation(text);
    try {
      // Async job pattern — kickoff returns immediately, we poll.
      const data = await runJob("genius", { situation: q });
      setResult(data);
    } catch (err) {
      setError(err?.message || "Wikifi is briefly resting. Try again in a moment.");
    } finally { setLoading(false); }
  };

  return (
    <Layout hideRightPanel>
      {/* Hero */}
      <PageHero
        testId="ask-hero"
        variant="bare"
        eyebrow="Wikifi · Genius"
        title="Tell me what you need."
        sub="A brilliant, caring AI advisor that listens to your life and hand-picks the three tools that will actually help. Speak or type — say anything."
      />

      {/* Big natural-language input */}
      <section className="curio-card p-6 mb-6" data-testid="ask-input-card">
        <label className="text-[10px] uppercase tracking-widest font-bold text-[#5B6478] block mb-2">Your situation</label>
        <textarea
          value={situation}
          onChange={e => setSituation(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) ask(); }}
          placeholder="e.g. I run a small business and I'm drowning in admin. What AI would help me the most?"
          rows={3}
          className="w-full text-lg leading-relaxed p-3 border-none focus:outline-none resize-none bg-transparent"
          data-testid="ask-textarea"
        />
        <div className="flex flex-wrap items-center justify-between gap-3 mt-2 pt-3 border-t border-[#E3E8F0]">
          <div className="flex items-center gap-2">
            {voiceSupported ? (
              <button
                onClick={toggleVoice}
                className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  listening ? "bg-coral border-coral text-white animate-pulse" :
                              "border-[#E3E8F0] text-[#5B6478] hover:border-[#0B1220] hover:text-[#0B1220]"
                }`}
                data-testid="voice-btn"
                aria-label={listening ? "Stop listening" : "Speak your situation"}
              >
                {listening ? <><MicOff size={12}/> Listening…</> : <><Mic size={12}/> Speak</>}
              </button>
            ) : (
              <span className="text-[11px] text-[#5B6478] italic">Voice supported on Chrome / Edge</span>
            )}
            <span className="text-[11px] text-[#5B6478] hidden sm:inline">or press ⌘+↵ to send</span>
          </div>
          <button
            onClick={() => ask()}
            disabled={loading || !situation.trim()}
            className="btn-coral text-sm disabled:opacity-50"
            data-testid="ask-send-btn"
          >
            {loading ? <><Loader2 size={13} className="animate-spin"/> Thinking…</> :
                       <>Wikifi</>}
          </button>
        </div>
      </section>

      {/* Preset chips */}
      {!result && !loading && (
        <section className="mb-8" data-testid="ask-presets">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#5B6478] mb-2">Or start with…</div>
          <div className="flex flex-wrap gap-2">
            {PRESET_SITUATIONS.map(p => (
              <button
                key={p.text}
                onClick={() => ask(p.text)}
                className="text-sm px-4 py-2.5 rounded-full bg-white border border-[#E3E8F0] hover:border-[#0B1220] hover:shadow-sm transition-all"
                data-testid={`preset-${p.text.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
              >
                <span className="mr-1.5">{p.emoji}</span>{p.text}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Error state */}
      {error && (
        <div className="curio-card p-5 border-2 border-rose-200 text-rose-700 mb-6">{error}</div>
      )}

      {/* Result */}
      {result && (
        <section data-testid="ask-result">
          <div className="curio-card p-6 mb-5 bg-[#F7F9FC]" data-testid="ask-situation-read">
            <div className="text-[10px] uppercase tracking-widest font-bold text-coral mb-2">Here's what I hear</div>
            <p className="font-display text-2xl italic leading-snug text-[#0B1220]">
              "{result.situation_read}"
            </p>
          </div>

          <div className="text-[10px] uppercase tracking-widest font-bold text-[#5B6478] mb-3">
            Three tools that will actually help
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="ask-recommendations">
            {result.recommendations.map((rec, i) => (
              <div key={rec.slug} className="curio-card p-5 flex flex-col" data-testid={`rec-${i}`}>
                <div className="flex items-start gap-3 mb-3">
                  <ToolLogo tool={rec} size={48} rounded="xl" className="flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold text-coral tracking-widest uppercase">Pick {i + 1} · {rec.letter_grade}</div>
                    <div className="font-display text-xl font-semibold leading-tight">{rec.name}</div>
                    <div className="text-xs text-[#5B6478]">{rec.category} · {rec.pricing}</div>
                  </div>
                </div>
                <p className="text-sm leading-relaxed text-[#0B1220]/85 mb-4 flex-1">
                  {rec.why}
                </p>
                <div className="text-xs bg-[#F7F9FC] rounded-lg p-2.5 mb-3">
                  <div className="text-[9px] uppercase tracking-widest font-bold text-[#5B6478] mb-0.5">First step</div>
                  <div className="font-semibold">{rec.first_step}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Link to={`/tool/${rec.slug}`} className="btn-ghost text-xs flex-1 justify-center" data-testid={`rec-details-${i}`}>
                    Details
                  </Link>
                  <a href={rec.url} target="_blank" rel="noreferrer"
                     className="btn-coral text-xs" data-testid={`rec-visit-${i}`}>
                    Visit <ExternalLink size={10}/>
                  </a>
                </div>
              </div>
            ))}
          </div>

          {result.follow_up_prompt && (
            <div className="curio-card p-5 flex items-start gap-3" data-testid="ask-followup">
              <div className="w-8 h-8 rounded-full bg-coral flex items-center justify-center flex-shrink-0">
                <Wand2 size={14} className="text-white"/>
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#5B6478] mb-1">One more question</div>
                <div className="text-sm mb-3">{result.follow_up_prompt}</div>
                <button onClick={() => ask(result.follow_up_prompt)}
                        className="btn-coral text-xs" data-testid="ask-followup-btn">
                  Answer this <ArrowRight size={11}/>
                </button>
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between text-xs text-[#5B6478]">
            <button onClick={() => { setResult(null); setSituation(""); }}
                    className="hover:text-[#0B1220] font-semibold"
                    data-testid="ask-again-btn">
              ← Ask something else
            </button>
            <Link to="/favorites" className="hover:text-[#0B1220] font-semibold inline-flex items-center gap-1">
              <Heart size={11}/> Save to My Stack
            </Link>
          </div>
        </section>
      )}

      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="skeleton h-56 rounded-2xl"/>)}
        </div>
      )}
    </Layout>
  );
}
